import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Bot, Loader2, ShoppingCart, Package, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { API } from "../config";
import axios from "axios";

const AIAssistant = () => {
  const { user, token } = useAuth();
  const { fetchCart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: "¡Hola! Soy el Agente de Ventas de Todo de Cartón. Puedo ayudarte a:\n\n• Encontrar la caja de archivo ideal para tu negocio\n• Agregar productos a tu carrito\n• Cotizar fulfillment\n• Resolver dudas sobre envíos y servicios\n\n¿En qué te puedo ayudar?"
      }]);
    }
  }, [isOpen, messages.length]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    if (!user) {
      setMessages(prev => [...prev,
        { role: "user", content: input },
        { role: "assistant", content: "Para usar el agente de ventas necesitas iniciar sesión. Ve a **Ingresar** en el menú para crear tu cuenta." }
      ]);
      setInput("");
      return;
    }

    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: userMsg,
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSessionId(response.data.session_id);

      const toolActions = response.data.tool_actions || [];
      const newMessages = [];

      if (toolActions.length > 0) {
        for (const action of toolActions) {
          if (action.tool === "add_to_cart" && action.result?.success) {
            newMessages.push({
              role: "tool",
              toolType: "cart_add",
              content: action.result.message
            });
            fetchCart();
          } else if (action.tool === "search_products" && action.result?.products) {
            newMessages.push({
              role: "tool",
              toolType: "products",
              products: action.result.products
            });
          } else if (action.tool === "get_cart") {
            newMessages.push({
              role: "tool",
              toolType: "cart_view",
              cart: action.result
            });
          } else if (action.tool === "quote_fulfillment") {
            newMessages.push({
              role: "tool",
              toolType: "fulfillment",
              quote: action.result
            });
          }
        }
      }

      newMessages.push({ role: "assistant", content: response.data.response });
      setMessages(prev => [...prev, ...newMessages]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Disculpa, tuve un problema técnico. ¿Puedes intentar de nuevo?"
      }]);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { label: "Ver productos", msg: "Muéstrame los productos disponibles" },
    { label: "Cotizar fulfillment", msg: "Necesito cotizar fulfillment para 50 pedidos al mes" },
    { label: "Mi carrito", msg: "¿Qué tengo en mi carrito?" },
  ];

  return (
    <>
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 w-14 h-14 bg-[#2E7D32] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#1B5E20] transition-colors z-40"
            data-testid="ai-assistant-btn"
          >
            <Sparkles size={24} />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 w-[400px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-4rem)] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col z-50 overflow-hidden"
            data-testid="ai-chat-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#2E7D32] to-[#1B5E20] text-white p-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                  <Sparkles size={18} />
                </div>
                <div>
                  <p className="font-bold text-sm">Agente de Ventas IA</p>
                  <p className="text-[10px] text-white/70 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse" />
                    En línea · Todo de Cartón
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="close-chat-btn">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, i) => (
                <div key={i}>
                  {msg.role === "tool" && msg.toolType === "cart_add" && (
                    <div className="flex justify-start mb-2">
                      <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs text-green-700 flex items-center gap-2 max-w-[85%]">
                        <ShoppingCart size={14} />
                        {msg.content}
                      </div>
                    </div>
                  )}
                  {msg.role === "tool" && msg.toolType === "products" && (
                    <div className="flex justify-start mb-2">
                      <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 max-w-[90%]">
                        <p className="text-[10px] font-bold text-stone-400 uppercase mb-2">Productos encontrados</p>
                        <div className="space-y-1.5">
                          {msg.products?.slice(0, 4).map((p) => (
                            <div key={p.id} className="flex items-center justify-between text-xs bg-white rounded-lg px-2.5 py-1.5 border border-stone-100">
                              <div>
                                <span className="font-semibold text-[#5D4037]">{p.name}</span>
                                <span className="text-stone-400 ml-1">{p.format}</span>
                              </div>
                              <span className="font-bold text-[#F57C00]">${p.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {msg.role === "tool" && msg.toolType === "fulfillment" && msg.quote?.monthly_estimate && (
                    <div className="flex justify-start mb-2">
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 max-w-[90%]">
                        <p className="text-[10px] font-bold text-blue-500 uppercase mb-2">Cotización Fulfillment</p>
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between"><span className="text-stone-600">Almacenamiento:</span><span className="font-semibold">${msg.quote.monthly_estimate.storage?.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-stone-600">Picking:</span><span className="font-semibold">${msg.quote.monthly_estimate.picking?.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-stone-600">Packing:</span><span className="font-semibold">${msg.quote.monthly_estimate.packing?.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-stone-600">Envío promedio:</span><span className="font-semibold">${msg.quote.monthly_estimate.shipping_avg?.toLocaleString()}</span></div>
                          <div className="flex justify-between pt-1 border-t border-blue-200 font-bold text-blue-700"><span>Total mensual:</span><span>${msg.quote.monthly_estimate.total_monthly?.toLocaleString()}</span></div>
                        </div>
                      </div>
                    </div>
                  )}
                  {(msg.role === "user" || msg.role === "assistant") && (
                    <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                        msg.role === "user"
                          ? "bg-[#2E7D32] text-white rounded-br-sm"
                          : "bg-stone-100 text-stone-800 rounded-bl-sm"
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-stone-100 rounded-2xl rounded-bl-sm px-4 py-2.5 flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin text-[#2E7D32]" />
                    <span className="text-xs text-stone-400">Consultando...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {messages.length <= 1 && user && (
              <div className="px-4 pb-2 flex gap-2 flex-wrap">
                {quickActions.map((qa, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(qa.msg); }}
                    className="text-[11px] bg-stone-100 hover:bg-stone-200 text-stone-600 px-3 py-1.5 rounded-full transition-colors"
                    data-testid={`quick-action-${i}`}
                  >
                    {qa.label}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="p-3 border-t border-stone-100 flex-shrink-0">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  placeholder={user ? "Escribe tu pregunta..." : "Inicia sesión para chatear"}
                  disabled={!user}
                  className="flex-1 h-10 px-3 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#2E7D32] disabled:bg-stone-50 disabled:text-stone-400"
                  data-testid="ai-chat-input"
                />
                <button
                  onClick={handleSend}
                  disabled={loading || !input.trim() || !user}
                  className="w-10 h-10 bg-[#2E7D32] text-white rounded-xl flex items-center justify-center hover:bg-[#1B5E20] disabled:opacity-50 transition-colors"
                  data-testid="ai-chat-send"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIAssistant;
